import {
    ITextFieldProps,
    TextField,
    getWindow,
    isIE11,
} from "@fluentui/react";
import React, {
    useCallback,
    useMemo,
    useState,
} from "react";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import "./RevealablePasswordField.scss";

export interface IRevealablePasswordFieldProps extends Omit<ITextFieldProps, "type" | "onRenderInput"> {
}

const detectBrowserNeedsRevealButton = (): boolean => {
    const win = getWindow();

    if (!win?.navigator) {
        return true;
    }

    const isEdge = /Edg/.test(win.navigator.userAgent || "");

    return !(isIE11() || isEdge);
};

const RevealablePasswordField = (props: IRevealablePasswordFieldProps) => {
    const [isRevealing, setIsRevealing] = useState(false);

    const browserNeedsRevealButton = useMemo(detectBrowserNeedsRevealButton, []);

    const toggleReveal = useCallback(() => {
        setIsRevealing((prev) => !prev);
    }, []);

    const handleMouseDown = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        // Prevent the TextField from losing focus, which would otherwise
        // require a second click to actually fire the onClick handler.
        event.preventDefault();
    }, []);

    const onRenderInput = useCallback((inputProps, defaultRenderer) => {
        const renderedInputProps = {
            ...inputProps,
            type: isRevealing ? "text" : "password",
        };

        return (
            <>
                {defaultRenderer(renderedInputProps)}
                {browserNeedsRevealButton && (
                    <button
                        type="button"
                        className="reveal-button ms-Button"
                        aria-pressed={isRevealing}
                        onMouseDown={handleMouseDown}
                        onClick={toggleReveal}
                        tabIndex={-1}
                    >
                        <span className="reveal-span">
                            <Visage2Icon icon={isRevealing ? "eye-slash" : "eye"} />
                        </span>
                    </button>
                )}
            </>
        );
    }, [browserNeedsRevealButton, handleMouseDown, isRevealing, toggleReveal]);

    return (
        <TextField
            {...props}
            type="password"
            onRenderInput={onRenderInput}
        />
    );
};

export default RevealablePasswordField;
